// /home/ec2-user/brand-insights-dashboard/frontend/src/App.js
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  AppBar, Toolbar, Typography, Box, Container,
  Button, IconButton, Alert, CircularProgress,
  Snackbar, Paper, Tabs, Tab, Chip
} from '@mui/material';
import {
  Refresh, Dashboard, Security, Close
} from '@mui/icons-material';

// Import componenti filtri
import { FiltersContainer, FilterDropdown } from './components/Filters';

// Import nuove scorecard
import { 
  SocialPerformanceMetrics, 
  PlatformDistribution,
  PostTypeDistribution,
  BrandDistribution,
  LoadingSkeleton,
  ErrorState
} from './components/Scorecards';

// Import componenti Insights
import { 
  AIImageAnalysisSection,
  AITextAnalysisSection,
  TimeToPostSection
} from './components/Charts';

// Import componenti Discovery
import DiscoveryGallery from './components/image_gallery';
import DiscoverySidebar from './components/DiscoverySidebar';
import { useAuth } from './hooks/useAuth';

// Backend API base URL
const API_BASE = 'https://brand-insights-backend.nexallai.com';

// ========== 🔧 FUNZIONI PER LA GESTIONE DEL COOKIE SSO ==========
const getEmailFromSSOCookie = () => {
  try {
    const cookies = document.cookie.split(';');
    const ssoCookie = cookies.find(c => c.trim().startsWith('nexallai_sso_session='));
    if (ssoCookie) {
      const cookieValue = decodeURIComponent(ssoCookie.split('=')[1]);
      const ssoData = JSON.parse(cookieValue);
      return ssoData.user?.email || null;
    }
  } catch (e) {
    console.error('Errore lettura cookie SSO:', e);
  }
  return null;
};

// ========== 🔧 FUNZIONE PER LA CHIAVE LOCALSTORAGE CON NAMESPACE ==========
const getStorageKey = () => {
  const email = getEmailFromSSOCookie();
  if (email) {
    const sanitized = email.replace(/[^a-zA-Z0-9]/g, '_');
    return `brand-insights-filters-${sanitized}`;
  }
  return 'brand-insights-filters';
};

// ========== 🔧 FUNZIONE PER INIZIALIZZARE I FILTRI ==========
// 🔑 MODIFICA: client_id viene SEMPRE dal cookie, mai da localStorage
const getInitialFilters = () => {
  const currentEmail = getEmailFromSSOCookie();
  
  if (!currentEmail) {
    return {
      posted_at_range: { start: null, end: null },
      brand: [],
      platform: [],
      post_type: [],
      client_id: '',
      project_id: '',
      selectedKpi: 'total_interactions'
    };
  }
  
  try {
    const key = getStorageKey();
    const saved = localStorage.getItem(key);
    if (saved) {
      const parsed = JSON.parse(saved);
      // 🔑 client_id SEMPRE dall'email del cookie, project_id dal localStorage
      return {
        posted_at_range: { start: null, end: null },
        brand: [],
        platform: [],
        post_type: [],
        client_id: currentEmail,
        project_id: parsed.project_id || '',
        selectedKpi: 'total_interactions'
      };
    }
  } catch (e) {
    console.error('Errore parsing localStorage:', e);
  }
  
  return {
    posted_at_range: { start: null, end: null },
    brand: [],
    platform: [],
    post_type: [],
    client_id: currentEmail,
    project_id: '',
    selectedKpi: 'total_interactions'
  };
};

const App = () => {
  const { username } = useAuth();

  // ========== STATO PRINCIPALI ==========
  const [loading, setLoading] = useState(false);
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  // ========== STATO PER TAB ATTIVO ==========
  const [activeTab, setActiveTab] = useState('reporting');
  
  // ========== STATO CACHE PER DATI ==========
  const [dataCache, setDataCache] = useState({
    reporting: {
      dashboardData: null,
      lastFetched: null,
      filtersHash: ''
    },
    insights: {
      dashboardData: null,
      insightsData: {},
      lastFetched: null,
      filtersHash: ''
    },
    discovery: {
      dashboardData: null,
      discoveryData: { 
        data: [], 
        pagination: { total: 0, limit: 20, offset: 0, has_more: false }, 
        filters_applied: {},
        stats: null
      },
      lastFetched: null,
      filtersHash: ''
    }
  });
  
  // ========== STATO VISIBILE PER UI ==========
  const [dashboardData, setDashboardData] = useState(null);
  const [insightsData, setInsightsData] = useState({});
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState(null);
  const [activeDimensionFilter, setActiveDimensionFilter] = useState(null);
  
  // ========== STATO PER MULTI-CLIENT ==========
  const [filters, setFilters] = useState(getInitialFilters);
  
  // ========== STATO PER OPZIONI FILTRI ==========
  const [filterOptions, setFilterOptions] = useState({
    brands: [],
    projects: [],
    clients: [],
    platforms: [],
    post_types: [],
    date_range: null,
    categories: [],
    content_types: [],
    primary_values: [],
    visual_styles: [],
    settings: [],
    target_implieds: [],
    cta_types: [],
    sentiments: [],
    intents: []
  });

  // ========== STATO PER DISCOVERY ==========
  const [discoveryFilters, setDiscoveryFilters] = useState({
    category: [],
    content_type: [],
    primary_value: [],
    visual_style: [],
    setting: [],
    target_implied: [],
    cta_type: [],
    sentiment: [],
    intent: [],
    min_engagement: 0,
    sort_by: 'total_engagement',
    sort_order: 'desc',
    limit: 20,
    offset: 0
  });

  const [discoveryData, setDiscoveryData] = useState({
    data: [],
    pagination: { total: 0, limit: 20, offset: 0, has_more: false },
    filters_applied: {},
    stats: null
  });

  const [discoveryLoading, setDiscoveryLoading] = useState(false);

  // ========== REF ==========
  const isInitialMount = useRef(true);
  const timeoutRef = useRef(null);
  const mountedRef = useRef(false);
  const hasAutoSelectedProjectRef = useRef(false);
  
  const prevFiltersRef = useRef({
    posted_at_range: { start: null, end: null },
    brand: [],
    platform: [],
    post_type: [],
    selectedKpi: 'total_interactions',
    project_id: '',
    client_id: ''
  });
  
  const filtersHashCache = useRef({});

  // ========== 1. FUNZIONI UTILITY ==========
  const showSnackbar = useCallback((message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
    console.log(`📢 ${severity.toUpperCase()}: ${message}`);
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  // 🔥 MODIFICA: salva solo project_id, NON il client_id
  const saveFiltersToStorage = useCallback((projectId) => {
    try {
      const key = getStorageKey();
      localStorage.setItem(key, JSON.stringify({
        project_id: projectId
        // 🔑 NON salvare client_id - viene sempre dal cookie
      }));
      console.log('💾 Filtri salvati (solo project_id):', { project_id: projectId, key });
    } catch (e) {
      console.error('Errore salvataggio localStorage:', e);
    }
  }, []);

  // ========== 🔥 EFFECT PER SINCRONIZZARE CLIENT_ID ==========
  useEffect(() => {
    const currentEmail = getEmailFromSSOCookie();
    if (!currentEmail) return;
    
    if (filters.client_id !== currentEmail) {
      console.log(`🔄 Sincronizzo client_id: ${filters.client_id} → ${currentEmail}`);
      setFilters(prev => ({
        ...prev,
        client_id: currentEmail,
        project_id: ''
      }));
      hasAutoSelectedProjectRef.current = false;
    }
  }, [filters.client_id]);

  // ========== 2. FUNZIONI API BASE ==========
  
  const fetchFilterOptions = useCallback(async (forceProjectId = null) => {
    try {
      setFilterOptionsLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      const projectId = forceProjectId || filters.project_id;
      
      if (projectId) {
        params.append('project_id', projectId);
      }
      
      if (filters.client_id) {
        params.append('client_id', filters.client_id);
      }
      
      params.append('limit_per_category', '100');
      
      const url = `${API_BASE}/api/v1/filters/options?${params}`;
      console.log('🌐 URL opzioni filtri:', url);
      
      const response = await fetch(url);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText.substring(0, 100)}`);
      }
      
      const apiData = await response.json();
      console.log('✅ Opzioni ricevute dal backend:', {
        clients: apiData.clients?.length || 0,
        projects: apiData.projects?.length || 0,
        brands: apiData.brands?.length || 0
      });
      
      const mappedOptions = {
        brands: (apiData.brands || []).map(b => ({
          value: b.value || b.id || b,
          label: b.label || b.name || b
        })),
        projects: (apiData.projects || []).map(p => ({
          value: p.value || p.id || p,
          label: p.label || p.name || p,
          created_at: p.created_at || null,
          client_id: p.client_id || null
        })),
        clients: (apiData.clients || []).map(c => ({
          value: c.value || c.id || c,
          label: c.label || c.name || c
        })),
        platforms: (apiData.platforms || []).map(p => ({
          value: p.value || p,
          label: p.label || p
        })),
        post_types: (apiData.post_types || []).map(pt => ({
          value: pt.value || pt,
          label: pt.label || pt
        })),
        date_range: apiData.date_range || null,
        categories: (apiData.categories || []).map(c => ({
          value: c.value || c.id || c,
          label: c.label || c.name || c
        })),
        content_types: (apiData.content_types || []).map(ct => ({
          value: ct.value || ct.id || ct,
          label: ct.label || ct.name || ct
        })),
        primary_values: (apiData.primary_values || []).map(pv => ({
          value: pv.value || pv.id || pv,
          label: pv.label || pv.name || pv
        })),
        visual_styles: (apiData.visual_styles || []).map(vs => ({
          value: vs.value || vs.id || vs,
          label: vs.label || vs.name || vs
        })),
        settings: (apiData.settings || []).map(s => ({
          value: s.value || s.id || s,
          label: s.label || s.name || s
        })),
        target_implieds: (apiData.target_implieds || []).map(ti => ({
          value: ti.value || ti.id || ti,
          label: ti.label || ti.name || ti
        })),
        cta_types: (apiData.cta_types || []).map(cta => ({
          value: cta.value || cta.id || cta,
          label: cta.label || cta.name || cta
        })),
        sentiments: (apiData.sentiments || []).map(s => ({
          value: s.value || s.id || s,
          label: s.label || s.name || s
        })),
        intents: (apiData.intents || []).map(i => ({
          value: i.value || i.id || i,
          label: i.label || i.name || i
        }))
      };
      
      setFilterOptions(mappedOptions);
      
      // AUTO-SELEZIONE PROGETTO
      if (isInitialMount.current && filters.client_id && !filters.project_id && mappedOptions.projects.length > 0) {
        if (!hasAutoSelectedProjectRef.current) {
          console.log('🎯 Auto-selezione progetto per client:', filters.client_id);
          
          const availableProjects = mappedOptions.projects.filter(p => 
            !p.client_id || p.client_id === filters.client_id
          );
          
          if (availableProjects.length > 0) {
            const sortedProjects = [...availableProjects].sort((a, b) => {
              const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
              const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
              return dateB - dateA;
            });
            
            const mostRecentProject = sortedProjects[0];
            
            console.log('✅ Progetto più recente trovato:', mostRecentProject.value);
            
            hasAutoSelectedProjectRef.current = true;
            
            setTimeout(() => {
              setFilters(prev => ({
                ...prev,
                project_id: mostRecentProject.value
              }));
              saveFiltersToStorage(mostRecentProject.value);
              showSnackbar(`Progetto "${mostRecentProject.label}" selezionato automaticamente`, 'success');
            }, 100);
          }
        }
      }
      
      if (isInitialMount.current) {
        isInitialMount.current = false;
      }
      
    } catch (err) {
      console.error('❌ Errore caricamento opzioni filtri:', err);
      setError(`Errore opzioni: ${err.message}`);
      showSnackbar(`Errore opzioni filtri: ${err.message}`, 'error');
    } finally {
      setFilterOptionsLoading(false);
    }
  }, [filters.project_id, filters.client_id, showSnackbar, saveFiltersToStorage]);

  const fetchDashboardData = useCallback(async () => {
    if (!filters.project_id || !filters.client_id) {
      console.log('⏭️ Salto fetch: progetto o client mancante');
      return null;
    }
    
    const params = new URLSearchParams();
    params.append('client_id', filters.client_id);
    params.append('project_id', filters.project_id);
    
    if (filters.posted_at_range?.start) {
      params.append('start_date', filters.posted_at_range.start.toISOString().split('T')[0]);
    }
    if (filters.posted_at_range?.end) {
      params.append('end_date', filters.posted_at_range.end.toISOString().split('T')[0]);
    }
    if (filters.brand && filters.brand.length > 0) {
      params.append('brand', filters.brand.join(','));
    }
    if (filters.platform && filters.platform.length > 0) {
      params.append('platform', filters.platform.join(','));
    }
    if (filters.post_type && filters.post_type.length > 0) {
      params.append('post_type', filters.post_type.join(','));
    }
    
    const url = `${API_BASE}/api/v1/analytics/overview?${params}`;
    console.log('🌐 URL dashboard data:', url);
    
    try {
      setLoading(true);
      const response = await fetch(url);
      
      if (!response.ok) {
        const errorText = await response.text();
        if (response.status === 403) {
          throw new Error(`Accesso negato al client ${filters.client_id}. Verifica i tuoi permessi.`);
        }
        throw new Error(`HTTP ${response.status}: ${errorText.substring(0, 100)}`);
      }
      
      const data = await response.json();
      console.log('✅ Dati dashboard ricevuti');
      return data;
      
    } catch (err) {
      console.error('❌ Errore caricamento dati dashboard:', err);
      showSnackbar(`Errore caricamento dati: ${err.message}`, 'error');
      return null;
    } finally {
      setLoading(false);
    }
  }, [filters, showSnackbar]);

  const fetchInsightsData = useCallback(async () => {
    if (!filters.project_id || !filters.client_id) {
      return {};
    }
    
    const insightsDimensions = [
      'category', 'content_type', 'primary_value', 'visual_style',
      'setting', 'target_implied', 'cta_type', 'sentiment', 'intent',
      'week_day_post', 'time_day_post', 'content_freshness'
    ];
    
    try {
      setInsightsLoading(true);
      setInsightsError(null);
      
      const allData = {};
      
      const fetchPromises = insightsDimensions.map(async (dimension) => {
        const params = new URLSearchParams();
        params.append('client_id', filters.client_id);
        params.append('project_id', filters.project_id);
        
        if (filters.posted_at_range?.start) {
          params.append('start_date', filters.posted_at_range.start.toISOString().split('T')[0]);
        }
        if (filters.posted_at_range?.end) {
          params.append('end_date', filters.posted_at_range.end.toISOString().split('T')[0]);
        }
        if (filters.brand && filters.brand.length > 0) {
          params.append('brand', filters.brand.join(','));
        }
        if (filters.platform && filters.platform.length > 0) {
          params.append('platform', filters.platform.join(','));
        }
        if (filters.post_type && filters.post_type.length > 0) {
          params.append('post_type', filters.post_type.join(','));
        }
        
        if (activeDimensionFilter && activeDimensionFilter.dimension !== dimension) {
          params.append(activeDimensionFilter.dimension, activeDimensionFilter.value);
        }
        
        params.append('dimension', dimension);
        
        const url = `${API_BASE}/api/v1/analytics/breakdown?${params}`;
        const response = await fetch(url);
        
        if (!response.ok) {
          if (response.status === 403) {
            throw new Error(`Accesso negato per ${dimension}`);
          }
          throw new Error(`HTTP ${response.status} per ${dimension}`);
        }
        
        const data = await response.json();
        return { dimension, data: data.items || [] };
      });
      
      const results = await Promise.all(fetchPromises);
      results.forEach(({ dimension, data }) => {
        allData[dimension] = data;
      });
      
      console.log('✅ Insights data fetched successfully');
      return allData;
      
    } catch (err) {
      console.error('❌ Errore caricamento insights:', err);
      setInsightsError(`Errore insights: ${err.message}`);
      showSnackbar(`Errore caricamento insights: ${err.message}`, 'error');
      return {};
    } finally {
      setInsightsLoading(false);
    }
  }, [filters, activeDimensionFilter, showSnackbar]);

  const fetchDiscoveryImages = useCallback(async () => {
    if (!filters.project_id || !filters.client_id) {
      console.log('⏭️ Salto fetch Discovery: progetto o client mancante');
      return { 
        data: [], 
        pagination: { total: 0, limit: 20, offset: 0, has_more: false }, 
        filters_applied: {},
        stats: null
      };
    }
    
    const params = new URLSearchParams();
    params.append('client_id', filters.client_id);
    params.append('project_id', filters.project_id);
    
    if (filters.posted_at_range?.start) {
      params.append('start_date', filters.posted_at_range.start.toISOString().split('T')[0]);
    }
    if (filters.posted_at_range?.end) {
      params.append('end_date', filters.posted_at_range.end.toISOString().split('T')[0]);
    }
    if (filters.brand && filters.brand.length > 0) {
      params.append('brand', filters.brand.join(','));
    }
    if (filters.platform && filters.platform.length > 0) {
      params.append('platform', filters.platform.join(','));
    }
    if (filters.post_type && filters.post_type.length > 0) {
      params.append('post_type', filters.post_type.join(','));
    }
    
    const discoveryFields = [
      'category', 'content_type', 'primary_value', 'visual_style',
      'setting', 'target_implied', 'cta_type', 'sentiment', 'intent'
    ];
    
    discoveryFields.forEach(field => {
      if (discoveryFilters[field] && discoveryFilters[field].length > 0) {
        params.append(field, discoveryFilters[field].join(','));
      }
    });
    
    if (discoveryFilters.min_engagement > 0) {
      params.append('min_engagement', discoveryFilters.min_engagement);
    }
    if (discoveryFilters.sort_by) {
      params.append('sort_by', discoveryFilters.sort_by);
    }
    if (discoveryFilters.sort_order) {
      params.append('sort_order', discoveryFilters.sort_order);
    }
    if (discoveryFilters.limit) {
      params.append('limit', discoveryFilters.limit);
    }
    if (discoveryFilters.offset) {
      params.append('offset', discoveryFilters.offset);
    }
    
    const url = `${API_BASE}/api/v2/images/unified?${params}`;
    console.log('🌐 URL Discovery images:', url);
    
    try {
      setDiscoveryLoading(true);
      const response = await fetch(url);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText.substring(0, 100)}`);
      }
      
      const data = await response.json();
      
      return {
        success: data.success !== false,
        data: data.data || [],
        pagination: data.pagination || { 
          total: 0, 
          limit: discoveryFilters.limit || 20, 
          offset: discoveryFilters.offset || 0, 
          has_more: false 
        },
        metadata: data.metadata || {},
        filters_applied: data.metadata?.filters_applied || {},
        stats: data.stats || null
      };
      
    } catch (err) {
      console.error('❌ Errore caricamento immagini Discovery:', err);
      showSnackbar(`Errore Discovery: ${err.message}`, 'error');
      return { 
        success: false,
        data: [], 
        pagination: { total: 0, limit: 20, offset: 0, has_more: false }, 
        filters_applied: {},
        stats: null
      };
    } finally {
      setDiscoveryLoading(false);
    }
  }, [filters, discoveryFilters, showSnackbar]);

  // ========== 3. FUNZIONI FETCH DATI ==========

  const fetchAllData = useCallback(async () => {
    try {
      const dashboardResult = await fetchDashboardData();
      if (dashboardResult) {
        setDashboardData(dashboardResult);
        
        if (activeTab === 'insights') {
          const insightsResult = await fetchInsightsData();
          setInsightsData(insightsResult);
        }
        
        if (activeTab === 'discovery') {
          const discoveryResult = await fetchDiscoveryImages();
          setDiscoveryData(discoveryResult);
        }
        
        showSnackbar('Dati aggiornati', 'success');
      }
    } catch (error) {
      console.error('❌ Errore fetchAllData:', error);
      showSnackbar(`Errore: ${error.message}`, 'error');
    }
  }, [activeTab, fetchDashboardData, fetchInsightsData, fetchDiscoveryImages, showSnackbar]);

  // ========== 4. HANDLERS ==========

  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
    console.log(`⚡ Tab cambiato a ${newValue}`);
  }, []);

  const memoizedFilterOptions = useMemo(() => filterOptions, [filterOptions]);

  const handleFilterChange = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  const handleSingleFilterChange = useCallback((key, value) => {
    console.log(`🔄 Aggiorno filtro SINGLE ${key}:`, value);
    
    if (key === 'client_id') {
      setFilters(prev => ({
        ...prev,
        client_id: value,
        project_id: '',
        brand: []
      }));
      // 🔑 Non salvare client_id in localStorage
      showSnackbar(`Client cambiato, seleziona un progetto`, 'info');
      
    } else if (key === 'project_id') {
      if (!filters.client_id) {
        showSnackbar('Seleziona prima un Client', 'warning');
        return;
      }
      
      setFilters(prev => ({
        ...prev,
        project_id: value,
        brand: []
      }));
      // 🔑 Salva solo project_id
      saveFiltersToStorage(value);
      showSnackbar(`Progetto selezionato`, 'success');
      
    } else {
      setFilters(prev => ({ ...prev, [key]: value }));
    }
  }, [filters.client_id, showSnackbar, saveFiltersToStorage]);

  const handleDiscoveryFilterChange = useCallback((key, value) => {
    console.log(`🔄 Aggiorno filtro Discovery ${key}:`, value);
    setDiscoveryFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleDiscoveryPageChange = useCallback((newOffset) => {
    console.log(`📄 Cambio pagina Discovery a offset: ${newOffset}`);
    setDiscoveryFilters(prev => ({ ...prev, offset: newOffset }));
  }, []);

  const handleResetDiscoveryFilters = useCallback(() => {
    console.log('🔄 Reset filtri Discovery');
    setDiscoveryFilters({
      category: [],
      content_type: [],
      primary_value: [],
      visual_style: [],
      setting: [],
      target_implied: [],
      cta_type: [],
      sentiment: [],
      intent: [],
      min_engagement: 0,
      sort_by: 'total_engagement',
      sort_order: 'desc',
      limit: 20,
      offset: 0
    });
    setDiscoveryData({ 
      data: [], 
      pagination: { total: 0, limit: 20, offset: 0, has_more: false }, 
      filters_applied: {},
      stats: null 
    });
    showSnackbar('Filtri Discovery resettati', 'info');
  }, [showSnackbar]);

  const handleDimensionFilter = useCallback((filterInfo) => {
    if (filterInfo === null) {
      setActiveDimensionFilter(null);
      showSnackbar('Filtro dimensionale rimosso', 'info');
    } else {
      setActiveDimensionFilter(filterInfo);
      showSnackbar(`Filtro applicato: ${filterInfo.label}`, 'success');
    }
  }, [showSnackbar]);

  const handleRefresh = useCallback(() => {
    console.log('🔄 Refresh manuale');
    fetchAllData();
  }, [fetchAllData]);

  const handleResetFilters = useCallback(() => {
    console.log('🔄 Reset filtri temporanei');
    
    setFilters(prev => ({
      ...prev,
      posted_at_range: { start: null, end: null },
      brand: [],
      platform: [],
      post_type: [],
      selectedKpi: 'total_interactions'
    }));
    
    setActiveDimensionFilter(null);
    
    if (activeTab === 'discovery') {
      handleResetDiscoveryFilters();
    }
    
    showSnackbar(`Filtri temporanei resettati (Client/Project preservati)`, 'info');
  }, [activeTab, handleResetDiscoveryFilters, showSnackbar]);

  // ========== 5. useEffect ==========

  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;
    
    console.log('🚀 App montata (una volta sola)');
    fetchFilterOptions();
    
    return () => {
      mountedRef.current = false;
    };
  }, [fetchFilterOptions]);

  useEffect(() => {
    if (!filters.project_id || !filters.client_id) return;
    
    fetchDashboardData().then(dashboardResult => {
      if (dashboardResult) {
        setDashboardData(dashboardResult);
      }
    });
    
  }, [filters.project_id, filters.client_id, fetchDashboardData]);

  useEffect(() => {
    if (isInitialMount.current) return;
    
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    
    timeoutRef.current = setTimeout(() => {
      console.log('🔄 Ricarico opzioni per:', { 
        client: filters.client_id, 
        project: filters.project_id 
      });
      fetchFilterOptions(filters.project_id);
    }, 500);
    
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [filters.project_id, filters.client_id, fetchFilterOptions]);

  useEffect(() => {
    if (isInitialMount.current) return;
    
    if (!filters.project_id || !filters.client_id) {
      console.log('⏭️ Salto fetch: progetto o client mancante');
      return;
    }
    
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    
    timeoutRef.current = setTimeout(() => {
      console.log('🚀 Avvio fetch dati per:', {
        client: filters.client_id,
        project: filters.project_id,
        tab: activeTab
      });
      fetchAllData();
    }, 800);
    
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [filters, activeDimensionFilter, fetchAllData, activeTab]);

  useEffect(() => {
    if (activeTab !== 'discovery') return;
    
    if (!filters.project_id || !filters.client_id) {
      console.log('⏭️ Salto fetch Discovery: progetto o client mancante');
      return;
    }
    
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    
    timeoutRef.current = setTimeout(() => {
      console.log('🚀 Fetch Discovery con paginazione');
      fetchDiscoveryImages().then(data => {
        if (data) {
          setDiscoveryData(data);
        }
      });
    }, 300);
    
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [
    activeTab,
    discoveryFilters.offset,
    discoveryFilters.limit,
    discoveryFilters.sort_by,
    discoveryFilters.sort_order,
    discoveryFilters.min_engagement,
    discoveryFilters.category,
    discoveryFilters.content_type,
    discoveryFilters.primary_value,
    discoveryFilters.visual_style,
    discoveryFilters.setting,
    discoveryFilters.target_implied,
    discoveryFilters.cta_type,
    discoveryFilters.sentiment,
    discoveryFilters.intent,
    filters.project_id,
    filters.client_id,
    fetchDiscoveryImages,
    showSnackbar
  ]);

  useEffect(() => {
    if (activeDimensionFilter) {
      const prevFilters = prevFiltersRef.current;
      const currentFilters = filters;
      
      const shouldReset = 
        currentFilters.posted_at_range?.start?.getTime() !== prevFilters.posted_at_range?.start?.getTime() ||
        currentFilters.posted_at_range?.end?.getTime() !== prevFilters.posted_at_range?.end?.getTime() ||
        JSON.stringify(currentFilters.brand) !== JSON.stringify(prevFilters.brand) ||
        JSON.stringify(currentFilters.platform) !== JSON.stringify(prevFilters.platform) ||
        JSON.stringify(currentFilters.post_type) !== JSON.stringify(prevFilters.post_type) ||
        currentFilters.selectedKpi !== prevFilters.selectedKpi ||
        currentFilters.project_id !== prevFilters.project_id ||
        currentFilters.client_id !== prevFilters.client_id;
      
      if (shouldReset) {
        setActiveDimensionFilter(null);
        showSnackbar('Filtro dimensionale rimosso (cambio filtri principali)', 'info');
      }
    }
    
    prevFiltersRef.current = {
      posted_at_range: filters.posted_at_range,
      brand: [...filters.brand],
      platform: [...filters.platform],
      post_type: [...filters.post_type],
      selectedKpi: filters.selectedKpi,
      project_id: filters.project_id,
      client_id: filters.client_id
    };
  }, [filters, activeDimensionFilter, showSnackbar]);

  // ========== RENDER ==========
  const isLoading = loading || filterOptionsLoading;
  const discoveryCurrentPage = Math.floor(discoveryFilters.offset / discoveryFilters.limit) + 1;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <AppBar position="fixed" color="default" elevation={1}>
        <Toolbar>
          <Dashboard sx={{ mr: 2, color: '#1976d2' }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2', mr: 4 }}>
            Brand Insights Dashboard
          </Typography>

          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            sx={{ 
              minHeight: 48,
              '& .MuiTab-root': {
                minHeight: 48,
                fontSize: '0.875rem',
                fontWeight: 600,
                textTransform: 'none',
                mr: 1
              }
            }}
          >
            <Tab 
              label="Reporting" 
              value="reporting" 
              sx={{
                color: activeTab === 'reporting' ? 'primary.main' : 'text.secondary',
                borderBottom: activeTab === 'reporting' ? '2px solid' : 'none',
                borderColor: 'primary.main'
              }}
            />
            <Tab 
              label="Insights" 
              value="insights" 
              sx={{
                color: activeTab === 'insights' ? 'primary.main' : 'text.secondary',
                borderBottom: activeTab === 'insights' ? '2px solid' : 'none',
                borderColor: 'primary.main'
              }}
            />
            <Tab 
              label="Discovery" 
              value="discovery" 
              sx={{
                color: activeTab === 'discovery' ? 'primary.main' : 'text.secondary',
                borderBottom: activeTab === 'discovery' ? '2px solid' : 'none',
                borderColor: 'primary.main'
              }}
            />
          </Tabs>

          <Box sx={{ flexGrow: 1 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mr: 2 }}>
            <FilterDropdown
              label="Client"
              value={filters.client_id || ''}
              onChange={(value) => handleSingleFilterChange('client_id', value)}
              options={filterOptions.clients || []}
              width={180}
              showAllOption={false}
              loading={isLoading}
              required={true}
              icon={Security}
              tooltip="Seleziona il client a cui hai accesso"
            />
            
            <FilterDropdown
              label="Project"
              value={filters.project_id || ''}
              onChange={(value) => handleSingleFilterChange('project_id', value)}
              options={filterOptions.projects || []}
              width={220}
              showAllOption={true}
              loading={isLoading}
              disabled={!filters.client_id}
              placeholder={filters.client_id ? "Seleziona progetto" : "Seleziona prima un Client"}
              tooltip={filters.client_id ? `Progetti disponibili per il client selezionato` : "Seleziona prima un Client"}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button 
              size="small" 
              onClick={activeTab === 'discovery' ? handleResetDiscoveryFilters : handleResetFilters}
              color="error"
              variant="outlined"
              disabled={
                (activeTab === 'discovery' && discoveryLoading) || 
                (activeTab !== 'discovery' && (isLoading || insightsLoading))
              }
            >
              {activeTab === 'discovery' ? 'Reset Discovery' : 'Reset Filtri'}
            </Button>
            
            <IconButton 
              onClick={handleRefresh} 
              color="primary" 
              title="Aggiorna dati"
              disabled={
                (loading && (activeTab === 'reporting' || activeTab === 'discovery')) || 
                (insightsLoading && activeTab === 'insights') || 
                !filters.project_id || 
                !filters.client_id
              }
            >
              {
                ((loading && (activeTab === 'reporting' || activeTab === 'discovery')) || 
                (insightsLoading && activeTab === 'insights')) ? 
                <CircularProgress size={24} /> : <Refresh />
              }
            </IconButton>
          </Box>
        </Toolbar>
        
        <Box sx={{ 
          borderTop: 1, 
          borderColor: 'divider',
          bgcolor: 'background.paper',
          minHeight: 30
        }}>
          <Container maxWidth="xl">
            <Box sx={{ 
              px: 3, 
              py: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap'
            }}>
              <FiltersContainer
                filters={filters}
                onFilterChange={handleFilterChange}
                onSingleFilterChange={handleSingleFilterChange}
                filterOptions={memoizedFilterOptions}  
                loading={isLoading}
                hideKpi={activeTab === 'reporting' || activeTab === 'discovery'}
              />
            </Box>
          </Container>
        </Box>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 3, mt: activeTab === 'discovery' ? 15 : 15 }}>
        
        {activeTab === 'reporting' ? (
          <>
            {isLoading && !dashboardData ? (
              <LoadingSkeleton />
            ) : error ? (
              <ErrorState error={error} onRetry={handleRefresh} />
            ) : !filters.client_id ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Security sx={{ fontSize: 60, color: '#9e9e9e', mb: 3, opacity: 0.5 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Seleziona un Client per iniziare
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  La dashboard mostra i dati del client selezionato
                </Typography>
              </Box>
            ) : !filters.project_id ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" color="text.secondary">
                  Seleziona un progetto per visualizzare la dashboard
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Client: {filters.client_id}
                </Typography>
              </Box>
            ) : (
              <>
                <SocialPerformanceMetrics 
                  dashboardData={dashboardData}
                  loading={loading}
                  filters={filters}
                />
                <PlatformDistribution filters={filters} loading={loading} />
                <PostTypeDistribution filters={filters} loading={loading} />
                <BrandDistribution filters={filters} loading={loading} />
              </>
            )}
          </>
        ) : activeTab === 'insights' ? (
          <>
            {!filters.client_id ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Security sx={{ fontSize: 60, color: '#9e9e9e', mb: 3, opacity: 0.5 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Seleziona un Client per visualizzare gli Insights
                </Typography>
              </Paper>
            ) : !filters.project_id ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                  Seleziona un progetto per visualizzare gli Insights
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Client: {filters.client_id}
                </Typography>
              </Paper>
            ) : (loading || insightsLoading) && (!dashboardData || Object.keys(insightsData).length === 0) ? (
              <LoadingSkeleton />
            ) : error || insightsError ? (
              <ErrorState error={error || insightsError} onRetry={handleRefresh} />
            ) : (
              <>
                <SocialPerformanceMetrics 
                  dashboardData={dashboardData}
                  loading={loading}
                  filters={filters}
                />

                <Paper sx={{ p: 3, mb: 4, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2 }}>
                      <Typography variant="h6" sx={{ color: 'white' }}>🖼️</Typography>
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight="bold">AI Image Analysis</Typography>
                      <Typography variant="body2" color="text.secondary">Analisi visiva dei contenuti</Typography>
                    </Box>
                  </Box>
                  <AIImageAnalysisSection
                    dimensionsData={insightsData}
                    selectedKpi={filters.selectedKpi}
                    onDimensionFilter={handleDimensionFilter}
                    activeDimensionFilter={activeDimensionFilter}
                    isLoading={insightsLoading}
                  />
                </Paper>

                <Paper sx={{ p: 3, mb: 4, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'secondary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2 }}>
                      <Typography variant="h6" sx={{ color: 'white' }}>📝</Typography>
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight="bold">AI Text Analysis</Typography>
                      <Typography variant="body2" color="text.secondary">Analisi semantica</Typography>
                    </Box>
                  </Box>
                  <AITextAnalysisSection
                    dimensionsData={insightsData}
                    selectedKpi={filters.selectedKpi}
                    onDimensionFilter={handleDimensionFilter}
                    activeDimensionFilter={activeDimensionFilter}
                    isLoading={insightsLoading}
                  />
                </Paper>

                <Paper sx={{ p: 3, mb: 4, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'success.main', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2 }}>
                      <Typography variant="h6" sx={{ color: 'white' }}>⏰</Typography>
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight="bold">Time to Post</Typography>
                      <Typography variant="body2" color="text.secondary">Timing ottimale</Typography>
                    </Box>
                  </Box>
                  <TimeToPostSection
                    dimensionsData={insightsData}
                    selectedKpi={filters.selectedKpi}
                    onDimensionFilter={handleDimensionFilter}
                    activeDimensionFilter={activeDimensionFilter}
                    isLoading={insightsLoading}
                  />
                </Paper>

                {activeDimensionFilter && (
                  <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50', borderLeft: '4px solid', borderColor: 'error.main' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold">Filtro attivo:</Typography>
                        <Chip label={`${activeDimensionFilter.label}`} color="error" size="small" onDelete={() => handleDimensionFilter(null)} />
                      </Box>
                      <Button size="small" startIcon={<Close />} onClick={() => handleDimensionFilter(null)}>
                        Rimuovi filtro
                      </Button>
                    </Box>
                  </Paper>
                )}
              </>
            )}
          </>
        ) : activeTab === 'discovery' ? (
          <>
            <SocialPerformanceMetrics 
              dashboardData={dashboardData}
              loading={loading}
              filters={filters}
            />

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, width: '100%', mt: 4 }}>
              <DiscoverySidebar
                discoveryFilters={discoveryFilters}
                filterOptions={filterOptions}
                onFilterChange={handleDiscoveryFilterChange}
                onReset={handleResetDiscoveryFilters}
                loading={discoveryLoading}
              />
              
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {discoveryData.data.length > 0 ? (
                  <DiscoveryGallery 
                    images={discoveryData.data}
                    pagination={discoveryData.pagination}
                    onPageChange={handleDiscoveryPageChange}
                    stats={discoveryData.stats}
                    loading={discoveryLoading}
                    activeFilters={{...filters, ...discoveryFilters}}
                    sortBy={discoveryFilters.sort_by}
                    currentPage={discoveryCurrentPage}
                    onSortChange={(newSortBy) => handleDiscoveryFilterChange('sort_by', newSortBy)}
                  />
                ) : (
                  <Paper sx={{ p: 6, textAlign: 'center', bgcolor: 'background.paper', borderRadius: 2 }}>
                    <Box sx={{ fontSize: 80, color: 'primary.main', mb: 3, opacity: 0.7 }}>🎨</Box>
                    <Typography variant="h5" gutterBottom fontWeight="medium">Discovery Gallery</Typography>
                    <Typography variant="body1" color="text.secondary" paragraph sx={{ maxWidth: 600, margin: '0 auto', mb: 3 }}>
                      Utilizza i filtri nella sidebar per scoprire contenuti rilevanti.
                      Puoi combinare più criteri per trovare immagini specifiche.
                    </Typography>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 3 }}>
                      <Button variant="outlined" onClick={handleResetDiscoveryFilters} size="large">Reset Filtri</Button>
                    </Box>
                  </Paper>
                )}
              </Box>
            </Box>
          </>
        ) : null}
        
      </Container>
    </Box>
  );
};

export default App;